import React, { useEffect, useState } from 'react';
import { Header, Icon, Modal, Step, Loader, Dimmer } from 'semantic-ui-react';
import { Formik } from 'formik';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { uuid } from 'uuidv4';
import Button from '../../../../../components/Button';
import {
  dangerNotification,
  successNotification,
  warningNotification
} from '../../../../../services/notification';
import { iRootDispatch, iRootState } from '../../../../../store';
import { AvailableBrokersAndEstatesState } from '../../../../../store/types/temp-types';
import { CustoDropDownItemProps } from '../../../../../components/types';
import { InsurerState } from '../../../../../types/InsurerState';
import { get } from '../../../../../services/storage';
import {
  createClaimEstate,
  udateClaimEstate,
  claimSubmitProps
} from '../../../../../services/claims';
import { getClaims } from '../../../../../services/claim';
import {
  postFileAndGetPreSignAxios,
  getFilesLinkAxios,
  deleteFilesLinkAxios,
  updateFileLabelAxios
} from '../../../../../services/file';
import { OTHER_FILE_KEY } from '../../create-claim-modal/Steps/DocumentsComponentStep';
import { ClaimEstatesStatusEnum } from '../../../../../enums/claim-estate-status.enum';
import { ClaimPropertyStatusEnum } from '../../../../../enums/claim-property-status.enum';
import { FormatDateYMD_UTC_IGNORE } from '../../../../../services/date';
import { maskCpfOrCnpj, maskPhoneNumber } from '../../../../../services/masks';
import { extractApiErrorMessages } from '../../../../../utils/apiErrorHandler';
import {
  sanitizeFilename,
  validateFileAccessibility
} from '../../../../../utils/fileUtils';
import { Callback } from '../../../../../types/Calback';
import {
  ClaimProductTypeEnum,
  getClaimProductEditModalTitle,
  getClaimProductModalTitle
} from '../../../enums/ClaimProductType.enum';
import { getOtherProductClaimInitialValues } from '../Formik/initialValues';
import {
  OtherProductClaimInitialValuesType,
  OtherProductClaimStep
} from '../Formik/types';
import BasicInfoStep from '../Steps/BasicInfoStep';
import CondominiumBasicInfoStep from '../Steps/CondominiumBasicInfoStep';
import { ClaimCondominiumType } from '../../../types/ClaimCondominiumTypes';
import ClaimDetailsStep from '../Steps/ClaimDetailsStep';
import DocumentsStep from '../Steps/DocumentsStep';
import { UserState } from '../../../../../types/UserState';
import { resolveClaimEstateId } from '../utils/resolveClaimEstateId';

const StepWrapper = styled(Step)`
  .title {
    color: ${props => props.theme.main.color} !important;
  }
`;

const MAX_RETRIES = 3;
const TIMEOUT_MS = 60000;

const stripHtml = (html?: string | null): string =>
  (html || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const formatEventDate = (value?: string | Date | null): string | null => {
  if (!value) return null;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return FormatDateYMD_UTC_IGNORE(date);
};

const isCondominiumProduct = (value?: ClaimProductTypeEnum | string): boolean =>
  value === ClaimProductTypeEnum.CONDOMINIUM;

const mapClaimCondominiumToFormValues = (claimEstate: any): ClaimCondominiumType => {
  const details =
    claimEstate.claimCondominium || claimEstate.claimCondominiumDetails || {};
  const syndic = details.syndic || {};

  return {
    document: maskCpfOrCnpj(details.document || ''),
    legalName: details.legalName || '',
    type: details.type || details.condominiumType || '',
    units:
      details.units === null || details.units === undefined ? '' : String(details.units),
    assessor: details.assessor || '',
    email: details.email || '',
    phone: details.phone ? maskPhoneNumber(details.phone) : '',
    syndic: {
      name: details.syndicName || details.responsibleName || syndic.name || '',
      document: maskCpfOrCnpj(
        details.syndicDocument || details.responsibleDocument || syndic.document || ''
      ),
      rg: details.syndicRg || details.responsibleRg || syndic.rg || '',
      birthDate: formatEventDate(
        details.syndicBirthDate || details.responsibleBirthDate || syndic.birthDate
      ),
      email: details.syndicEmail || details.responsibleEmail || syndic.email || '',
      phone: details.syndicPhone
        ? maskPhoneNumber(details.syndicPhone)
        : details.responsiblePhone
        ? maskPhoneNumber(details.responsiblePhone)
        : syndic.phone
        ? maskPhoneNumber(syndic.phone)
        : ''
    }
  };
};

const mapExistingFiles = (filesResponse: any) => {
  const urls = filesResponse?.urls || filesResponse || [];
  if (!Array.isArray(urls)) return [];

  return urls.map((u: any) => ({
    id: u.id,
    key: u.name,
    name: typeof u.name === 'string' ? u.name.split('/').pop() : u.name,
    url: u.link,
    option: u.name,
    label: u.label || ''
  }));
};

type CreateOtherProductClaimModalProps = {
  open: boolean;
  productType: ClaimProductTypeEnum;
  onClose: () => void;
  availableBrokersAndEstates: AvailableBrokersAndEstatesState;
  user?: UserState;
  draftId?: string;
  cloneId?: string;
  editId?: string;
  users?: UserState[];
  onlyModal?: boolean;
  onCloseCallback?: Callback;
};

const mapClaimToFormValues = (
  claimEstate: any,
  productType: ClaimProductTypeEnum
): OtherProductClaimInitialValuesType => {
  const resolvedProductType =
    (claimEstate.productType as ClaimProductTypeEnum) || productType;
  const claimCondominium = mapClaimCondominiumToFormValues(claimEstate);
  const isCondominium = isCondominiumProduct(resolvedProductType);

  return {
    productType: (claimEstate.productType as ClaimProductTypeEnum) || productType,
    claimEstate: {
      id: claimEstate.id,
      status: claimEstate.status,
      observations: claimEstate.observations || '',
      responsibleId: claimEstate.responsible?.id || claimEstate.responsibleId || null,
      estateResponsibleId:
        claimEstate.estateResponsible?.id || claimEstate.estateResponsibleId || null,
      insurerId: claimEstate.insurer?.id || claimEstate.insurerId,
      estateId: claimEstate.estate?.id || claimEstate.estateId,
      createdBy: claimEstate.createdBy?.id || claimEstate.createdBy || '',
      policyNumber: claimEstate.policyNumber || null,
      insuranceBroker: claimEstate.insuranceBroker || '',
      insuranceStatus: claimEstate.insuranceStatus || '',
      insuranceExpirationDate: claimEstate.insuranceExpirationDate || null,
      processNumber: claimEstate.processNumber || null,
      claimCode: claimEstate.claimCode || null,
      notificationEmails: claimEstate.notificationEmails
        ? typeof claimEstate.notificationEmails === 'string'
          ? claimEstate.notificationEmails.split(';')
          : claimEstate.notificationEmails
        : [''],
      returnFollowup: claimEstate.returnFollowup,
      propertyPublicId: claimEstate.propertyPublicId || undefined,
      productType: claimEstate.productType || productType,
      eventDate: claimEstate.eventDate || null,
      eventTime: claimEstate.eventTime || '',
      eventDescription: claimEstate.eventDescription || '',
      coverages: Array.isArray(claimEstate.coverages) ? claimEstate.coverages : []
    },
    claimTenant: {
      id: claimEstate.claimTenant?.id,
      document: isCondominium
        ? claimCondominium.document
        : maskCpfOrCnpj(claimEstate.claimTenant?.document || ''),
      fullName: isCondominium
        ? claimCondominium.legalName
        : claimEstate.claimTenant?.fullName || '',
      email: isCondominium
        ? claimCondominium.email
        : claimEstate.claimTenant?.email || '',
      cellphone: isCondominium
        ? claimCondominium.phone
        : claimEstate.claimTenant?.cellphone || ''
    },
    claimProperty: {
      id: claimEstate.claimProperty?.id,
      zipCode: claimEstate.claimProperty?.zipCode || '',
      street: claimEstate.claimProperty?.street || '',
      number: claimEstate.claimProperty?.number || '',
      complement: claimEstate.claimProperty?.complement || '',
      district: claimEstate.claimProperty?.district || '',
      city: claimEstate.claimProperty?.city || '',
      state: claimEstate.claimProperty?.state || '',
      country: claimEstate.claimProperty?.country || 'Brasil',
      propertyCode: claimEstate.claimProperty?.propertyCode || '',
      status: claimEstate.claimProperty?.status || ClaimPropertyStatusEnum.occupied,
      deliveryKeysDate: claimEstate.claimProperty?.deliveryKeysDate || undefined
    },
    claimCondominium,
    claimFiles: []
  };
};

const CreateOtherProductClaimModal = ({
  open,
  productType,
  onClose,
  availableBrokersAndEstates,
  user,
  draftId,
  cloneId,
  editId,
  users = []
}: CreateOtherProductClaimModalProps) => {
  const history = useHistory();
  const estates: CustoDropDownItemProps[] = availableBrokersAndEstates.estates;
  const defaultEstateId = resolveClaimEstateId(
    user,
    availableBrokersAndEstates,
    estates
  );
  const insurers: InsurerState[] = (availableBrokersAndEstates.insurers || []).map(
    (insurer: any) => insurer.value
  );
  const isEditMode = Boolean(editId);

  const [step, setStep] = useState<OtherProductClaimStep>('basic_info');
  const [registerId, setRegisterId] = useState(uuid());
  const [loading, setLoading] = useState(false);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [existingFiles, setExistingFiles] = useState<any[]>([]);
  const [highlightMissingLabels, setHighlightMissingLabels] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{
    current: number;
    total: number;
    fileName: string;
  } | null>(null);
  const [initialValues, setInitialValues] = useState(
    getOtherProductClaimInitialValues(productType, defaultEstateId)
  );

  useEffect(() => {
    if (!open) return;

    setStep('basic_info');
    if (editId) {
      setRegisterId(editId);
      fetchEditData(editId);
    } else if (draftId) {
      setRegisterId(draftId);
      fetchDraftData(draftId);
    } else if (cloneId) {
      setRegisterId(uuid());
      fetchCloneData(cloneId);
    } else {
      setRegisterId(uuid());
      setInitialValues(
        getOtherProductClaimInitialValues(
          productType,
          resolveClaimEstateId(user, availableBrokersAndEstates, estates)
        )
      );
      setExistingFiles([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, productType, draftId, cloneId, editId]);

  async function fetchDraftData(id: string) {
    setLoading(true);
    try {
      const claimEstate = await getClaims({ id });
      setInitialValues(mapClaimToFormValues(claimEstate, productType));

      try {
        const files = await getFilesLinkAxios(id, 'CLAIM_ESTATES');
        setExistingFiles(mapExistingFiles(files));
      } catch (err) {
        console.log('Erro ao buscar arquivos do rascunho', err);
        setExistingFiles([]);
      }

      setStep('document_attachment');
    } catch (err) {
      console.error(err);
      dangerNotification('Erro', 'Não foi possível carregar o rascunho.');
    } finally {
      setLoading(false);
    }
  }

  async function fetchEditData(id: string) {
    setLoading(true);
    try {
      const claimEstate = await getClaims({ id });
      setInitialValues(mapClaimToFormValues(claimEstate, productType));

      try {
        const files = await getFilesLinkAxios(id, 'CLAIM_ESTATES');
        setExistingFiles(mapExistingFiles(files));
      } catch (err) {
        console.log('Erro ao buscar arquivos do sinistro', err);
        setExistingFiles([]);
      }

      setStep('basic_info');
    } catch (err) {
      console.error(err);
      dangerNotification('Erro', 'Não foi possível carregar o sinistro para edição.');
    } finally {
      setLoading(false);
    }
  }

  async function fetchCloneData(sourceClaimId: string) {
    setLoading(true);
    try {
      const claimEstate = await getClaims({ id: sourceClaimId });
      const resolvedProductType =
        (claimEstate.productType as ClaimProductTypeEnum) || productType;
      const cloneCondominium = mapClaimCondominiumToFormValues(claimEstate);
      const cloneIsCondominium = isCondominiumProduct(resolvedProductType);

      setInitialValues({
        productType: resolvedProductType,
        claimEstate: {
          id: '',
          status: ClaimEstatesStatusEnum.created,
          createdBy: '',
          observations: '',
          responsibleId: null,
          estateResponsibleId: null,
          policyNumber: claimEstate.policyNumber || null,
          insuranceBroker: claimEstate.insuranceBroker || '',
          insuranceStatus: claimEstate.insuranceStatus || '',
          insuranceExpirationDate: claimEstate.insuranceExpirationDate || null,
          processNumber: null,
          claimCode: null,
          estateId: claimEstate.estate?.id || claimEstate.estateId || '',
          insurerId: claimEstate.insurer?.id || claimEstate.insurerId || '',
          notificationEmails: claimEstate.notificationEmails
            ? claimEstate.notificationEmails.split(';')
            : [''],
          productType: resolvedProductType,
          eventDate: claimEstate.eventDate || null,
          eventTime: claimEstate.eventTime || '',
          eventDescription: claimEstate.eventDescription || '',
          coverages: [],
          propertyPublicId: claimEstate.propertyPublicId || undefined
        },
        claimTenant: {
          document: cloneIsCondominium
            ? cloneCondominium.document
            : maskCpfOrCnpj(claimEstate.claimTenant?.document || ''),
          fullName: cloneIsCondominium
            ? cloneCondominium.legalName
            : claimEstate.claimTenant?.fullName || '',
          email: cloneIsCondominium
            ? cloneCondominium.email
            : claimEstate.claimTenant?.email || '',
          cellphone: cloneIsCondominium
            ? cloneCondominium.phone
            : claimEstate.claimTenant?.cellphone || ''
        },
        claimCondominium: cloneCondominium,
        claimProperty: {
          zipCode: claimEstate.claimProperty?.zipCode || '',
          street: claimEstate.claimProperty?.street || '',
          number: claimEstate.claimProperty?.number || '',
          complement: claimEstate.claimProperty?.complement || '',
          district: claimEstate.claimProperty?.district || '',
          city: claimEstate.claimProperty?.city || '',
          state: claimEstate.claimProperty?.state || '',
          country: claimEstate.claimProperty?.country || 'Brasil',
          propertyCode: claimEstate.claimProperty?.propertyCode || '',
          status: claimEstate.claimProperty?.status || ClaimPropertyStatusEnum.occupied,
          deliveryKeysDate: undefined
        },
        claimFiles: []
      });
      setExistingFiles([]);
      setStep('basic_info');
    } catch (err) {
      console.error(err);
      dangerNotification('Erro', 'Não foi possível carregar o sinistro para clonagem.');
    } finally {
      setLoading(false);
    }
  }

  const handleClose = () => {
    setStep('basic_info');
    setExistingFiles([]);
    onClose();
  };

  const ensureEstateId = (
    values: OtherProductClaimInitialValuesType,
    setFieldValue?: (field: string, value: any) => void
  ): boolean => {
    if (values.claimEstate.estateId) return true;

    const resolved = resolveClaimEstateId(
      user,
      availableBrokersAndEstates,
      estates
    );
    if (!resolved) return false;

    values.claimEstate.estateId = resolved;
    setFieldValue?.('claimEstate.estateId', resolved);
    return true;
  };

  const validateBasicInfo = (
    values: OtherProductClaimInitialValuesType,
    setFieldValue?: (field: string, value: any) => void
  ): boolean => {
    const isCondominium = isCondominiumProduct(
      values.productType || values.claimEstate.productType
    );

    if (isCondominium) {
      const condominium = values.claimCondominium;
      if (
        !condominium.document ||
        !condominium.legalName ||
        !condominium.type ||
        !condominium.syndic.name ||
        !condominium.syndic.email ||
        !condominium.syndic.phone
      ) {
        warningNotification(
          'Atenção',
          'Preencha os campos obrigatórios das Informações do Condomínio e do Síndico.'
        );
        return false;
      }

      if (!values.claimEstate.policyNumber) {
        warningNotification('Atenção', 'Informe o Número da Apólice.');
        return false;
      }

      if (!values.claimEstate.insuranceBroker) {
        warningNotification('Atenção', 'Informe o Corretor Seguro.');
        return false;
      }
    } else {
      const { document, fullName, email, cellphone } = values.claimTenant;
      if (!document || !fullName || !email || !cellphone) {
        warningNotification(
          'Atenção',
          'Preencha todos os campos obrigatórios das Informações do Segurado.'
        );
        return false;
      }
    }

    if (!values.claimEstate.insurerId) {
      warningNotification('Atenção', 'Selecione a Seguradora/Garantidora.');
      return false;
    }

    if (!ensureEstateId(values, setFieldValue)) {
      warningNotification('Atenção', 'Selecione a Imobiliária.');
      return false;
    }

    const property = values.claimProperty;
    if (
      !property.zipCode ||
      !property.street ||
      !property.district ||
      !property.city ||
      !property.state ||
      !property.number
    ) {
      warningNotification(
        'Atenção',
        'Preencha todos os campos obrigatórios do Endereço.'
      );
      return false;
    }

    const emails = (values.claimEstate.notificationEmails as string[]).filter(
      e => e && e.trim()
    );
    if (!emails.length) {
      warningNotification('Atenção', 'Informe ao menos um e-mail para notificação.');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (emails.some(e => !emailRegex.test(e))) {
      warningNotification('Atenção', 'Existem e-mails de notificação inválidos.');
      return false;
    }

    return true;
  };

  const validateClaimDetails = (values: OtherProductClaimInitialValuesType): boolean => {
    if (!stripHtml(values.claimEstate.eventDescription)) {
      warningNotification('Atenção', 'A Descrição do Evento é obrigatória.');
      return false;
    }

    if (!values.claimEstate.coverages || values.claimEstate.coverages.length < 1) {
      warningNotification('Atenção', 'Selecione ao menos uma cobertura acionada.');
      return false;
    }

    return true;
  };

  const validateDraft = (
    values: OtherProductClaimInitialValuesType,
    setFieldValue?: (field: string, value: any) => void
  ): boolean => {
    if (!ensureEstateId(values, setFieldValue)) {
      warningNotification(
        'Atenção',
        'Selecione a Imobiliária para salvar o rascunho.'
      );
      return false;
    }

    if (!values.claimEstate.coverages || values.claimEstate.coverages.length < 1) {
      warningNotification(
        'Atenção',
        'Selecione ao menos uma cobertura acionada para salvar o rascunho.'
      );
      return false;
    }

    const emails = (values.claimEstate.notificationEmails as string[]).filter(
      e => e && e.trim()
    );
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (emails.some(e => !emailRegex.test(e))) {
      warningNotification('Atenção', 'Existem e-mails de notificação inválidos.');
      return false;
    }

    return true;
  };

  const buildPayload = (
    values: OtherProductClaimInitialValuesType,
    status: ClaimEstatesStatusEnum
  ): claimSubmitProps => {
    const notificationEmails = Array.isArray(values.claimEstate.notificationEmails)
      ? (values.claimEstate.notificationEmails as string[])
          .filter(e => e && e.trim())
          .join(';')
      : values.claimEstate.notificationEmails;

    const claimId = values.claimEstate.id || editId || draftId || registerId;
    const isCondominium = isCondominiumProduct(
      values.productType || values.claimEstate.productType
    );
    const condominium = values.claimCondominium;

    return {
      claimEstate: {
        id: claimId,
        status,
        observations: values.claimEstate.observations || '',
        responsibleId: values.claimEstate.responsibleId,
        estateResponsibleId: values.claimEstate.estateResponsibleId || null,
        insurerId: values.claimEstate.insurerId || null,
        estateId: values.claimEstate.estateId,
        policyNumber: values.claimEstate.policyNumber || null,
        insuranceBroker: values.claimEstate.insuranceBroker || '',
        insuranceStatus: values.claimEstate.insuranceStatus || '',
        insuranceExpirationDate: values.claimEstate.insuranceExpirationDate || null,
        processNumber: values.claimEstate.processNumber || null,
        claimCode: values.claimEstate.claimCode || null,
        returnFollowup: values.claimEstate.returnFollowup,
        notificationEmails,
        propertyPublicId: values.claimEstate.propertyPublicId,
        productType: values.productType || productType,
        eventDate: formatEventDate(values.claimEstate.eventDate as any),
        eventTime: values.claimEstate.eventTime || null,
        eventDescription: values.claimEstate.eventDescription || null,
        coverages: values.claimEstate.coverages || []
      } as any,
      claimProperty: {
        ...values.claimProperty,
        ...(values.claimProperty.id ? { id: values.claimProperty.id } : {}),
        country: values.claimProperty.country || 'Brasil',
        deliveryKeysDate: undefined
      } as any,
      claimTenant: {
        ...values.claimTenant,
        ...(values.claimTenant.id ? { id: values.claimTenant.id } : {}),
        document: (isCondominium
          ? condominium.document
          : values.claimTenant.document || ''
        ).replace(/[^0-9]/g, ''),
        fullName: isCondominium ? condominium.legalName : values.claimTenant.fullName,
        email: isCondominium ? condominium.email : values.claimTenant.email,
        cellphone: isCondominium ? condominium.phone : values.claimTenant.cellphone
      },
      ...(isCondominium
        ? {
            claimCondominium: {
              ...condominium,
              document: (condominium.document || '').replace(/[^0-9]/g, ''),
              units:
                condominium.units === '' || condominium.units === null
                  ? undefined
                  : Number(condominium.units),
              syndic: {
                ...condominium.syndic,
                document: (condominium.syndic.document || '').replace(/[^0-9]/g, ''),
                birthDate: condominium.syndic.birthDate || undefined
              }
            }
          }
        : {}),
      claimValues: [],
      userId: get('uid') || undefined
    };
  };

  const validateOtherLabels = (
    claimFiles: { name: string; customLabel?: string }[]
  ): boolean => {
    const missingNew = claimFiles.some(
      f => f.name === OTHER_FILE_KEY && !String(f.customLabel || '').trim()
    );
    const missingExisting = existingFiles.some(
      f =>
        (f.key === OTHER_FILE_KEY || f.option === OTHER_FILE_KEY) &&
        !String(f.label || '').trim()
    );
    if (missingNew || missingExisting) {
      setHighlightMissingLabels(true);
      warningNotification(
        'Atenção',
        'Informe um título para cada arquivo do tipo Outros (máx. 30 caracteres).'
      );
      return false;
    }
    setHighlightMissingLabels(false);
    return true;
  };

  async function submitFiles(
    files: {
      file: File;
      name: string;
      customLabel?: string;
    }[]
  ) {
    if (!files.length) return;

    setLoadingFiles(true);
    setUploadProgress({ current: 0, total: files.length, fileName: '' });

    const fileToBlob = async (theFile: File): Promise<Blob> => {
      try {
        const arrayBuffer = await theFile.arrayBuffer();
        return new Blob([new Uint8Array(arrayBuffer)], { type: theFile.type });
      } catch (error) {
        const errorMessage = (error as Error)?.message || '';
        if (
          errorMessage.includes('could not be found') ||
          errorMessage.includes('NotFoundError') ||
          errorMessage.includes('NotReadableError')
        ) {
          throw new Error(
            `O arquivo "${theFile.name}" não está mais acessível. Por favor, remova-o e adicione novamente.`
          );
        }
        throw new Error(
          `Erro ao ler o arquivo "${theFile.name}": ${
            errorMessage || 'Erro desconhecido'
          }`
        );
      }
    };

    const uploadWithRetry = async (
      uploadURL: string,
      blob: Blob,
      fileName: string
    ): Promise<Response> => {
      let lastError: Error | null = null;

      for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

          const response = await fetch(uploadURL, {
            method: 'PUT',
            body: blob,
            signal: controller.signal
          });

          clearTimeout(timeoutId);

          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }

          return response;
        } catch (error) {
          lastError = error as Error;

          if (attempt < MAX_RETRIES) {
            const waitTime = 1000 * Math.pow(2, attempt - 1);
            await new Promise(resolve => setTimeout(resolve, waitTime));
          }
        }
      }

      throw new Error(
        `Falha ao enviar arquivo "${fileName}" após ${MAX_RETRIES} tentativas: ${
          lastError?.message || 'Erro desconhecido'
        }`
      );
    };

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        let fileId: string | null = null;

        setUploadProgress({
          current: i + 1,
          total: files.length,
          fileName: file.file.name
        });

        try {
          await validateFileAccessibility(file.file);

          let response: any;
          let lastPreSignError: Error | null = null;
          let preSignSuccess = false;

          for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
            try {
              response = await postFileAndGetPreSignAxios({
                filename: sanitizeFilename(file.file.name),
                contentType: file.file.type,
                referencedTableName: 'CLAIM_ESTATES',
                referencedTableId: registerId,
                name: file.name,
                label:
                  file.name === OTHER_FILE_KEY
                    ? String(file.customLabel || '').trim() || null
                    : null
              });
              preSignSuccess = true;
              fileId = response.id;
              break;
            } catch (error) {
              lastPreSignError = error as Error;
              if (attempt < MAX_RETRIES) {
                const waitTime = 1000 * Math.pow(2, attempt - 1);
                await new Promise(resolve => setTimeout(resolve, waitTime));
              }
            }
          }

          if (!preSignSuccess || !response) {
            throw new Error(
              `Falha ao obter link de upload para "${
                file.file.name
              }" após ${MAX_RETRIES} tentativas: ${
                lastPreSignError?.message || 'Erro desconhecido'
              }`
            );
          }

          const blob = await fileToBlob(file.file);
          await uploadWithRetry(response.uploadURL, blob, file.file.name);
        } catch (error) {
          if (fileId) {
            try {
              await deleteFilesLinkAxios(fileId);
            } catch (deleteError) {
              console.error(
                `[Upload] Falha ao remover entrada órfã ${fileId}:`,
                deleteError
              );
            }
          }
          throw error;
        }
      }
    } finally {
      setLoadingFiles(false);
      setUploadProgress(null);
    }
  }

  async function handleDeleteExistingFile(id: string) {
    try {
      setLoading(true);
      await deleteFilesLinkAxios(id);
      setExistingFiles(prev => prev.filter(f => f.id !== id));
      successNotification('Sucesso', 'Arquivo removido com sucesso!');
    } catch (err) {
      dangerNotification('Erro', 'Erro ao remover arquivo.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  async function handleSaveDraft(
    values: OtherProductClaimInitialValuesType,
    setFieldValue?: (field: string, value: any) => void
  ) {
    if (!validateDraft(values, setFieldValue)) return;
    if (!validateOtherLabels(values.claimFiles)) return;

    setLoading(true);
    try {
      await submitFiles(values.claimFiles);
      const payload = buildPayload(values, ClaimEstatesStatusEnum.draft);

      if (values.claimEstate.id || draftId) {
        await udateClaimEstate(values.claimEstate.id || draftId!, payload);
        successNotification('Sucesso', 'Rascunho atualizado com sucesso!');
      } else {
        await createClaimEstate(payload);
        successNotification('Sucesso', 'Rascunho salvo com sucesso!');
      }

      handleClose();
      history.push('claim_estates');
    } catch (err) {
      console.error(err);
      dangerNotification(
        'Erro',
        extractApiErrorMessages(err, 'Erro ao salvar rascunho'),
        8000
      );
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit(
    values: OtherProductClaimInitialValuesType,
    setFieldValue?: (field: string, value: any) => void
  ) {
    if (!validateBasicInfo(values, setFieldValue) || !validateClaimDetails(values)) return;
    if (!validateOtherLabels(values.claimFiles)) return;

    setLoading(true);
    try {
      await submitFiles(values.claimFiles);
      const submitStatus = isEditMode
        ? (values.claimEstate.status as ClaimEstatesStatusEnum) ||
          ClaimEstatesStatusEnum.created
        : ClaimEstatesStatusEnum.created;
      const payload = buildPayload(values, submitStatus);

      if (values.claimEstate.id || editId || draftId) {
        await udateClaimEstate(values.claimEstate.id || editId || draftId!, payload);
      } else {
        await createClaimEstate(payload);
      }

      successNotification(
        'Sucesso',
        isEditMode
          ? 'Solicitação de sinistro atualizada com sucesso!'
          : 'Solicitação de abertura de sinistro realizada com sucesso!'
      );
      handleClose();
      if (!isEditMode) {
        history.push('claim_estates');
      }
    } catch (err) {
      console.error(err);
      dangerNotification(
        isEditMode ? 'Erro ao Atualizar Sinistro' : 'Erro ao Criar Sinistro',
        extractApiErrorMessages(
          err,
          isEditMode ? 'Erro ao atualizar sinistro' : 'Erro ao criar sinistro'
        ),
        8000
      );
    } finally {
      setLoading(false);
    }
  }

  const handleNext = (
    values: OtherProductClaimInitialValuesType,
    setFieldValue?: (field: string, value: any) => void
  ) => {
    if (step === 'basic_info') {
      if (!validateBasicInfo(values, setFieldValue)) return;
      setStep('claim_details');
      return;
    }

    if (step === 'claim_details') {
      if (!validateClaimDetails(values)) return;
      setStep('document_attachment');
      return;
    }

    handleSubmit(values, setFieldValue);
  };

  const handleBack = () => {
    if (step === 'basic_info') {
      handleClose();
      return;
    }
    if (step === 'claim_details') {
      setStep('basic_info');
      return;
    }
    setStep('claim_details');
  };

  const modalTitleProductType =
    (initialValues.productType as ClaimProductTypeEnum) || productType;
  const modalTitle = isEditMode
    ? getClaimProductEditModalTitle(modalTitleProductType)
    : getClaimProductModalTitle(modalTitleProductType);

  return (
    <Modal
      style={{ height: 'auto', width: '70vw', zIndex: 10 }}
      closeIcon
      closeOnDimmerClick={false}
      open={open}
      onClose={handleClose}
    >
      <Header>
        <Icon name="file alternate outline" />
        {modalTitle}
      </Header>

      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={() => true}
        onSubmit={() => true}
      >
        {formikProps => (
          <>
            <Modal.Content scrolling>
              <Dimmer active={loadingFiles}>
                <Loader>
                  {uploadProgress ? (
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ marginBottom: '8px', fontWeight: 'bold' }}>
                        Enviando documentos ({uploadProgress.current} de{' '}
                        {uploadProgress.total})
                      </div>
                      <div style={{ fontSize: '12px', opacity: 0.9 }}>
                        {uploadProgress.fileName}
                      </div>
                    </div>
                  ) : (
                    'Enviando documentos...'
                  )}
                </Loader>
              </Dimmer>
              <Dimmer active={loading}>
                <Loader indeterminate>Comunicando solicitação...</Loader>
              </Dimmer>

              <div style={{ width: '100%' }}>
                <Step.Group widths={3}>
                  <StepWrapper
                    active={step === 'basic_info'}
                    title="Informações básicas"
                    description="Dados do segurado e informações iniciais"
                    icon={{ color: 'grey', name: 'user' }}
                  />
                  <StepWrapper
                    active={step === 'claim_details'}
                    title="Detalhes do sinistro"
                    description="Informações específicas da ocorrência"
                    icon={{ color: 'grey', name: 'file alternate outline' }}
                  />
                  <StepWrapper
                    active={step === 'document_attachment'}
                    title="Documentos"
                    description="Anexos opcionais do sinistro"
                    icon={{ color: 'grey', name: 'upload' }}
                  />
                </Step.Group>
              </div>

              {step === 'basic_info' &&
                (isCondominiumProduct(
                  formikProps.values.productType ||
                    formikProps.values.claimEstate.productType
                ) ? (
                  <CondominiumBasicInfoStep
                    {...formikProps}
                    estates={estates}
                    insurers={insurers}
                    availableBrokersAndEstates={availableBrokersAndEstates}
                    isEditMode={isEditMode}
                    users={users}
                  />
                ) : (
                  <BasicInfoStep
                    {...formikProps}
                    estates={estates}
                    insurers={insurers}
                    availableBrokersAndEstates={availableBrokersAndEstates}
                    user={user}
                    isEditMode={isEditMode}
                    users={users}
                  />
                ))}
              {step === 'claim_details' && <ClaimDetailsStep {...formikProps} />}
              {step === 'document_attachment' && (
                <DocumentsStep
                  {...formikProps}
                  insurers={insurers}
                  existingFiles={existingFiles}
                  onDeleteExisting={handleDeleteExistingFile}
                  highlightMissingLabels={highlightMissingLabels}
                  onExistingLabelChange={(id, label) => {
                    setExistingFiles(prev =>
                      prev.map(f => (f.id === id ? { ...f, label } : f))
                    );
                  }}
                  onUpdateExistingLabel={async (id, label) => {
                    await updateFileLabelAxios(id, label);
                    setExistingFiles(prev =>
                      prev.map(f => (f.id === id ? { ...f, label } : f))
                    );
                    successNotification('Sucesso', 'Título do arquivo atualizado.');
                  }}
                />
              )}
            </Modal.Content>

            <Modal.Actions style={{ display: 'flex', justifyContent: 'space-between' }}>
              {step === 'document_attachment' && !isEditMode ? (
                <Button
                  color="grey"
                  onClick={() =>
                    handleSaveDraft(formikProps.values, formikProps.setFieldValue)
                  }
                  style={{ marginRight: 'auto' }}
                >
                  <Icon name="save" /> Salvar como Rascunho
                </Button>
              ) : (
                <div />
              )}
              <div style={{ display: 'flex' }}>
                <Button color="red" onClick={handleBack}>
                  <Icon name="remove" />
                  {step === 'basic_info' ? 'Sair' : 'Voltar'}
                </Button>
                <Button
                  color="green"
                  onClick={() =>
                    handleNext(formikProps.values, formikProps.setFieldValue)
                  }
                >
                  <Icon name="checkmark" />
                  {step === 'document_attachment'
                    ? isEditMode
                      ? 'Salvar Alterações'
                      : draftId
                      ? 'Atualizar e Solicitar'
                      : 'Solicitar'
                    : 'Próximo'}
                </Button>
              </div>
            </Modal.Actions>
          </>
        )}
      </Formik>
    </Modal>
  );
};

const mapState = (state: iRootState) => ({
  availableBrokersAndEstates: state.availableBrokersAndEstates,
  user: state.user
});

const mapDispatch = (dispatch: iRootDispatch) => ({
  updateAvailableBrokersAndEstates: (availableBrokers: AvailableBrokersAndEstatesState) =>
    dispatch.availableBrokersAndEstates.updateAvailableBrokersAndEstates(availableBrokers)
});

export default connect(mapState, mapDispatch)(CreateOtherProductClaimModal);
